import { CREATIVE_TYPE } from '@ad-lucy/config/creatives';
import { useCreativesConfig } from '@ad-lucy/context/creativesConfig';
import type { KnownBreakpoints } from '@ad-lucy/context/ThemeProvider';
import { EventsUtils } from '@ad-lucy/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useBreakpoint = (): KnownBreakpoints | null => {
  const { breakpoints, id: creativeId } = useCreativesConfig();

  const knownBreakpoints = useMemo(() => {
    if (creativeId === CREATIVE_TYPE.SA) {
      return {
        billboard: breakpoints.billboard,
        mobile: breakpoints.mobile,
        hpa: breakpoints.hpa,
        sitebar: breakpoints.sitebar,
      };
    }

    return {
      mobile: breakpoints.mobile,
      hpa: breakpoints.hpa,
      sitebar: breakpoints.sitebar,
    };
  }, [breakpoints, creativeId]);

  const [breakpoint, setBreakpoint] = useState<KnownBreakpoints | null>(() => {
    for (const [name, query] of Object.entries(knownBreakpoints)) {
      if (query && typeof query === 'string' && window.matchMedia(query).matches) {
        return name as KnownBreakpoints;
      }
    }
    return null;
  });

  const listener = useCallback((): void => {
    for (const [name, query] of Object.entries(knownBreakpoints)) {
      if (query && typeof query === 'string' && window.matchMedia(query).matches) {
        setBreakpoint(name as KnownBreakpoints);
        break;
      }
    }
  }, [knownBreakpoints]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const { debounce } = EventsUtils;
    const debounced = debounce(listener, 200);
    window.addEventListener('resize', debounced);
    listener();

    return () => {
      window.removeEventListener('resize', debounced);
    };
  }, [knownBreakpoints, listener]);

  return breakpoint;
};
